import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import '../styles/global-tables.scss';

export type Column<T> = {
  key: keyof T | string;
  label: string;
  width?: number | string;
  includeInChildRow?: boolean;
  includeInParentRow?: boolean;
  render?: (row: T) => React.ReactNode;
};

export type ExpandableTableProps<T> = {
  rows: T[];
  columns: Column<T>[];
  getChildRows?: (row: T) => T[];
  isRowOpen?: (row: T) => boolean;
  onRowToggle?: (row: T) => void;
  rowKey: (row: T) => string | number;
  renderParentRow?: (row: T) => React.ReactNode;
};

export function createColumn<T>(column: Column<T>): Column<T> {
  // @ts-ignore
  return {
    includeInChildRow: column?.includeInChildRow ?? true,
    includeInParentRow: column?.includeInParentRow ?? true,
    ...column,
  };
}

export function ExpandableTable<T extends object>({
                                                    rows,
                                                    columns,
                                                    getChildRows,
                                                    isRowOpen,
                                                    onRowToggle,
                                                    rowKey,
                                                    renderParentRow,
                                                  }: ExpandableTableProps<T>) {
  return (
    <TableContainer component={Paper} className="table-container">
      <Table size="small" className="custom-table">
        {!renderParentRow && (
          <TableHead>
            <TableRow>
              {getChildRows && <TableCell className="expand-icon-cell" />}
              {columns.map((col) => (
                <TableCell key={col.key as string} sx={col.width ? { width: col.width } : undefined}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}

        <TableBody>
          {rows.map((row) => {
            const childRows = getChildRows ? getChildRows(row) : [];
            const open = isRowOpen ? isRowOpen(row) : false;

            return (
              <React.Fragment key={rowKey(row)}>
                <TableRow hover className={renderParentRow ? 'parent-row' : ''}>
                  {getChildRows && (
                    <TableCell className="expand-icon-cell">
                      {childRows.length > 0 && onRowToggle && (
                        <IconButton size="small" onClick={() => onRowToggle(row)}>
                          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                  {renderParentRow
                    ? <TableCell colSpan={columns.length}>{renderParentRow(row)}</TableCell>
                    : (() => {
                        const parentCols = columns.filter(col => col.includeInParentRow !== false);
                        const hiddenCount = columns.length - parentCols.length;
                        return parentCols.map((col, idx) => {
                          const isLast = idx === parentCols.length - 1;
                          return (
                            <TableCell key={col.key as string} colSpan={isLast ? 1 + hiddenCount : 1}>
                              {col.render ? col.render(row) : (row[col.key as keyof T] as React.ReactNode)}
                            </TableCell>
                          );
                        });
                      })()
                  }
                </TableRow>

                {open && childRows.length > 0 && (
                  <>
                    {renderParentRow && (
                      <TableRow className="child-header-row">
                        <TableCell className="expand-icon-cell" />
                        {columns.map((col) => (
                          col.includeInChildRow !== false
                            ? <TableCell key={col.key as string}><strong>{col.label}</strong></TableCell>
                            : <TableCell key={col.key as string} />
                        ))}
                      </TableRow>
                    )}
                    {childRows.map((child) => (
                      <TableRow key={rowKey(child)} className="child-row">
                        {getChildRows && <TableCell className="expand-icon-cell" />}
                        {columns.map((col) => (
                          <TableCell key={col.key as string}>
                            {col.render && col.includeInChildRow ? col.render(child) : <></>}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
